'use client';

import { makeAutoObservable, runInAction } from 'mobx';
import {
  UpdateUserDocument,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  DeletePaymentMethodDocument,
  DeletePaymentMethodMutation,
  DeletePaymentMethodMutationVariables,
  PaymentMethodsDocument,
  PaymentMethodsQuery,
  PaymentMethodsQueryVariables,
  SetDefaultPaymentMethodDocument,
  SetDefaultPaymentMethodMutation,
  SetDefaultPaymentMethodMutationVariables,
} from './mutations.generated';
import { Address } from './address';
import { PaymentMethod, type PaymentMethodJSON } from './payment-method';
import { apiClient } from '@instamenu/context-api';
import { debounce } from 'lodash-es';
import { captureClientException } from '@instamenu/posthog/client';

export type UserJSON = Partial<
  NonNullable<NonNullable<UpdateUserMutation['updateUser']>['user']>
> & {
  id: string;
  paymentMethods?: PaymentMethodJSON[];
};
const byKey = new Map<string, User>();

type UserUpdate = UpdateUserMutationVariables['input'];

function mergeUpdates(older: UserUpdate | undefined, newer: UserUpdate | undefined): UserUpdate {
  const merged = { ...older, ...newer };
  if (older?.deliveryAddressesAttributes && newer?.deliveryAddressesAttributes) {
    merged.deliveryAddressesAttributes = [
      ...older.deliveryAddressesAttributes,
      ...newer.deliveryAddressesAttributes,
    ];
  }
  return merged;
}

export class User {
  json!: UserJSON;
  _deliveryAddresses: Address[] = [];
  _paymentMethods: PaymentMethod[] = [];

  private constructor(json: UserJSON) {
    this.updateFromJson(json);
    makeAutoObservable(this);
  }

  static fromJSON(json: UserJSON) {
    const key = json.id;
    let instance = byKey.get(key);
    if (!instance) {
      instance = new User(json);
      byKey.set(instance.id, instance);
    } else {
      instance.updateFromJson(json);
    }
    return instance;
  }

  updateFromJson(json: Partial<UserJSON>) {
    const unsaved = { ...this.retryVars, ...this.inFlightSync, ...this.syncVars };
    const preserved = Object.fromEntries(
      Object.keys(unsaved)
        .filter((key) => key in (this.json ?? {}))
        .map((key) => [key, this.json[key as keyof UserJSON]]),
    );
    this.json = { ...this.json, ...json, ...preserved };
    if (json.deliveryAddresses) {
      if (unsaved.deliveryAddressesAttributes) {
        for (const saved of json.deliveryAddresses) {
          const local = this._deliveryAddresses.find((address) => address.json.key === saved.key);
          if (local && !local.id) local.updateFromJson({ id: saved.id });
        }
      } else {
        this._deliveryAddresses = json.deliveryAddresses.map((a) => Address.fromJSON(a));
      }
    }
    if (json.paymentMethods) {
      this._paymentMethods = json.paymentMethods.map((pm) => PaymentMethod.fromJSON(pm));
    }
  }

  get employers() {
    return this.json.employers ?? [];
  }

  get name() {
    return this.json.name;
  }
  get id() {
    return this.json.id;
  }
  get email() {
    return this.json.email;
  }
  get phone() {
    return this.json.phone;
  }
  get deliveryAddresses() {
    return this._deliveryAddresses;
  }
  get paymentMethods() {
    return this._paymentMethods;
  }
  get defaultDeliveryAddress() {
    return (
      this.deliveryAddresses.find((a) => a.id === this.defaultDeliveryAddressId) ??
      this.deliveryAddresses[0]
    );
  }
  get defaultDeliveryAddressId() {
    return this.json.defaultDeliveryAddressId;
  }
  get defaultPaymentMethod() {
    return this.json.defaultPaymentMethod;
  }
  get firstName() {
    return this.json.firstName;
  }
  get lastName() {
    return this.json.lastName;
  }

  addAddress(address: Address, save = true) {
    this.deliveryAddresses.push(address);
    this.json.defaultDeliveryAddressId = address.id;
    if (save)
      return this.sync({
        deliveryAddressesAttributes: [{ ...address.asDeliveryAddressAttributes(), default: true }],
      });
    return Promise.resolve(true);
  }

  discardAddress(address: Address) {
    if (!address.id) return Promise.resolve(false);
    this._deliveryAddresses = this.deliveryAddresses.filter((a) => a.key !== address.key);
    if (this.defaultDeliveryAddressId === address.id)
      this.json.defaultDeliveryAddressId = undefined;
    return this.sync({ deliveryAddressesAttributes: [{ id: address.id, discard: true }] });
  }

  setPaymentMethods(paymentMethods: PaymentMethodJSON[]) {
    this._paymentMethods = paymentMethods.map((pm) => PaymentMethod.fromJSON(pm));
  }

  async refreshPaymentMethods() {
    const res = await apiClient()
      .query<PaymentMethodsQuery, PaymentMethodsQueryVariables>(PaymentMethodsDocument, {})
      .toPromise();
    if (res.data?.paymentMethods) this.setPaymentMethods(res.data.paymentMethods);
  }

  async deletePaymentMethod(id: string) {
    this._paymentMethods = this.paymentMethods.filter((pm) => pm.id !== id);
    await apiClient()
      .mutation<
        DeletePaymentMethodMutation,
        DeletePaymentMethodMutationVariables
      >(DeletePaymentMethodDocument, { input: { id } })
      .toPromise();
  }

  async setDefaultPaymentMethod(id: string) {
    this._paymentMethods = this.paymentMethods.map((pm) =>
      PaymentMethod.fromJSON({ ...pm.json, isDefault: pm.id === id }),
    );
    await apiClient()
      .mutation<
        SetDefaultPaymentMethodMutation,
        SetDefaultPaymentMethodMutationVariables
      >(SetDefaultPaymentMethodDocument, { input: { id } })
      .toPromise();
  }

  syncVars?: UserUpdate;
  private inFlightSync?: UserUpdate;
  private retryVars?: UserUpdate;
  private pendingWaiters: Array<(success: boolean) => void> = [];
  syncError?: string;

  get canRetrySync() {
    return !!this.retryVars;
  }

  sync(vars: UserUpdate): Promise<boolean> {
    this.syncVars = mergeUpdates(this.syncVars, vars);
    if (this.retryVars) {
      const remaining = { ...this.retryVars };
      for (const key of Object.keys(vars) as Array<keyof UserUpdate>) delete remaining[key];
      this.retryVars = Object.keys(remaining).length ? remaining : undefined;
    }
    if (!this.retryVars) this.syncError = undefined;
    const result = new Promise<boolean>((resolve) => this.pendingWaiters.push(resolve));
    this.debouncedSync();
    return result;
  }

  private debouncedSync = debounce(() => {
    void this.flushSync();
  }, 500);

  private async flushSync() {
    if (this.inFlightSync || !this.syncVars) return;

    const input = this.syncVars;
    const waiters = this.pendingWaiters;
    this.syncVars = undefined;
    this.pendingWaiters = [];
    this.inFlightSync = input;

    try {
      const res = await apiClient()
        .mutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument, { input })
        .toPromise();
      if (res.error) throw res.error;
      const updatedUser = res.data?.updateUser?.user;
      if (!updatedUser) throw new Error('Unable to save your changes');

      runInAction(() => {
        this.inFlightSync = undefined;
        this.updateFromJson(updatedUser);
        if (!this.retryVars) this.syncError = undefined;
        waiters.forEach((resolve) => resolve(true));
        if (this.syncVars) this.debouncedSync();
      });
    } catch (error) {
      captureClientException(error, {
        source: 'context-user.sync',
        properties: { fields: Object.keys(input) },
      });
      runInAction(() => {
        this.inFlightSync = undefined;
        const networkError =
          typeof error === 'object' &&
          error !== null &&
          'networkError' in error &&
          !!error.networkError;
        const { deliveryAddressesAttributes, ...retryable } = input;
        if (networkError && Object.keys(retryable).length) {
          const retry = mergeUpdates(this.retryVars, retryable);
          for (const key of Object.keys(this.syncVars ?? {}) as Array<keyof UserUpdate>)
            delete retry[key];
          this.retryVars = Object.keys(retry).length ? retry : undefined;
        }
        this.syncError = deliveryAddressesAttributes
          ? 'Address save could not be confirmed. Refresh before trying again.'
          : 'Could not save your changes.';
        waiters.forEach((resolve) => resolve(false));
        if (this.syncVars) this.debouncedSync();
      });
    }
  }

  retrySync() {
    if (!this.retryVars) return Promise.resolve(false);
    const retry = this.retryVars;
    this.retryVars = undefined;
    return this.sync(retry);
  }

  update(input: UserUpdate) {
    const fields = Object.fromEntries(
      Object.entries(input).filter(([key]) =>
        ['firstName', 'lastName', 'name', 'email', 'phone', 'defaultDeliveryAddressId'].includes(
          key,
        ),
      ),
    );
    this.json = { ...this.json, ...fields };
    return this.sync(input);
  }

  static async init(vars: UpdateUserMutationVariables['input']) {
    const res = await apiClient()
      .mutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument, {
        input: { ...vars, hasConfirmedName: true },
      })
      .toPromise();
    if (!res.data?.updateUser?.user) throw new Error('could not initialize the user');
    return User.fromJSON(res.data.updateUser.user);
  }

  get permissions() {
    if (!this.json.employments) return {};
    return this.json.employments?.reduce((acc: { [restaurantId: string]: string[] }, emp) => {
      acc[emp.restaurant.id] = emp.permissions ?? [];
      return acc;
    }, {});
  }
}
